import { Alert, Button, Table, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { formatDays, formatMoney, formatPercent, formatScore } from "../format"
import { fetchMemberTypes } from "./api"
import type { MemberQuery, MemberTypesResponse, TypeRow } from "./types"

const MIN_COMPANY = 5
const MIN_MEMBER = 3

interface Props {
  memberId: string
  query: MemberQuery
  onOpenType: (row: TypeRow) => void
}

// A member's figures per project type beside the company's for the same type.
// Below the company comparison is a measured difference, never a cause (spec §4).
export function TypesView({ memberId, query, onOpenType }: Props) {
  const { t } = useTranslation()
  const [data, setData] = useState<MemberTypesResponse | null>(null)
  const [failed, setFailed] = useState(false)
  const key = JSON.stringify([query.grain, query.from, query.to])

  useEffect(() => {
    let current = true
    setFailed(false)
    fetchMemberTypes(memberId, query)
      .then((body) => current && setData(body))
      .catch(() => current && setFailed(true))
    return () => {
      current = false
    }
  }, [memberId, key])

  const money = (value: number | null) =>
    data?.amounts_visible ? formatMoney(value) : "—"
  const companyScore = (row: TypeRow, kind: "schedule" | "financial") => {
    const benchmark = row.benchmark[kind]
    if (benchmark.available) return formatScore(row.company[`${kind}_score`])
    const company = benchmark.reason === "insufficient_benchmark_sample"
    return (
      <Typography.Text type="secondary" className="text-xs">
        {t(`teamPerformance.types.${benchmark.reason}`)} (
        {t("teamPerformance.member.coverageValue", {
          count: company ? benchmark.company_sample : benchmark.member_sample,
          total: company ? MIN_COMPANY : MIN_MEMBER
        })}
        )
      </Typography.Text>
    )
  }

  return (
    <>
      {query.projectType && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t("teamPerformance.types.everyType")}
        />
      )}
      {failed && (
        <Alert
          type="error"
          showIcon
          message={t("teamPerformance.states.loadFailed")}
        />
      )}
      <Table<TypeRow>
        rowKey="id"
        size="middle"
        loading={!data && !failed}
        dataSource={data?.types ?? []}
        scroll={{ x: 1000 }}
        pagination={false}
        locale={{ emptyText: t("teamPerformance.member.noProjects") }}
        columns={[
          {
            key: "name",
            title: t("teamPerformance.filters.projectType"),
            fixed: "left",
            render: (_, row) => (
              <Button
                type="link"
                className="px-0"
                onClick={() => onOpenType(row)}
                aria-label={t("teamPerformance.member.viewProjectsOf", {
                  name: row.name
                })}
              >
                {row.name}
              </Button>
            )
          },
          {
            key: "projects",
            title: t("teamPerformance.table.projects"),
            align: "right",
            render: (_, r) => r.projects
          },
          {
            key: "on_time",
            title: t("teamPerformance.table.onTime"),
            align: "right",
            render: (_, r) => formatPercent(r.summary.on_time_percent)
          },
          {
            key: "avg_delay",
            title: t("teamPerformance.table.avgDelay"),
            align: "right",
            render: (_, r) => formatDays(r.summary.avg_delay_days)
          },
          {
            key: "schedule",
            title: t("teamPerformance.table.schedule"),
            align: "right",
            render: (_, r) => formatScore(r.schedule_score)
          },
          {
            key: "company_schedule",
            title: t("teamPerformance.types.company"),
            align: "right",
            render: (_, r) => companyScore(r, "schedule")
          },
          {
            key: "over",
            title: t("teamPerformance.table.overBudget"),
            align: "right",
            render: (_, r) => r.summary.overrun_projects
          },
          {
            key: "gap",
            title: t("teamPerformance.table.revenueGap"),
            align: "right",
            render: (_, r) => money(r.summary.gap_amount)
          },
          {
            key: "financial",
            title: t("teamPerformance.table.financial"),
            align: "right",
            render: (_, r) => formatScore(r.financial_score)
          },
          {
            key: "company_financial",
            title: t("teamPerformance.types.company"),
            align: "right",
            render: (_, r) => companyScore(r, "financial")
          }
        ]}
      />
      <Typography.Paragraph type="secondary" className="mt-3 text-xs">
        {t("teamPerformance.types.note")}
      </Typography.Paragraph>
    </>
  )
}
